import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { api, fmtMoney, fmtDate, safeSnippet } from '../lib/api.js';
import PmGrid from '../components/PmGrid.jsx';

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, canManageEquipment } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [logBody, setLogBody] = useState('');
  const [hours, setHours] = useState('');
  const [manuals, setManuals] = useState([]);
  const [manualType, setManualType] = useState('operator');
  const [uploading, setUploading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState('keyword'); // 'keyword' | 'qa'
  const [qaAnswer, setQaAnswer] = useState(null);
  const fileInputRef = useRef(null);

  async function load() {
    try {
      const d = await api.get(`/api/equipment/${id}`);
      setData(d);
      const { manuals } = await api.get(`/api/equipment/${id}/manuals`);
      setManuals(manuals);
    } catch (e) {
      setErr(e.message);
    }
  }

  useEffect(() => { load(); }, [id]);

  const [uploadResult, setUploadResult] = useState(null);
  const [reExtractingId, setReExtractingId] = useState(null);
  // Bump to force PmGrid to re-fetch (after approve / extract / etc.)
  const [pmRev, setPmRev] = useState(0);
  function bumpPm() { setPmRev(v => v + 1); }
  const [hunt, setHunt] = useState(null);     // { candidates, equipment } | null
  const [hunting, setHunting] = useState(false);
  const [downloadingUrl, setDownloadingUrl] = useState(null);
  // Tasks just approved via "let me enter when each was last done" (null = form hidden)
  const [lastDoneTasks, setLastDoneTasks] = useState(null);

  async function huntManuals() {
    setHunting(true); setErr('');
    try {
      const r = await api.post(`/api/equipment/${id}/hunt-manuals`, { types: ['operator', 'service'] });
      setHunt(r);
    } catch (e) { setErr(`Hunt failed: ${e.message}`); }
    finally { setHunting(false); }
  }
  async function downloadCandidate(c) {
    setDownloadingUrl(c.url); setErr(''); setUploadResult(null);
    try {
      const r = await api.post(`/api/equipment/${id}/manuals/from-url`, { url: c.url, type: c.type, filename: c.title });
      setUploadResult({
        page_count: r.page_count,
        pm_tasks_extracted: r.pm_tasks_extracted ?? 0,
        pm_extract_error: r.pm_extract_error ?? null,
      });
      // Refresh manuals list and PM grid
      const m = await api.get(`/api/equipment/${id}/manuals`);
      setManuals(m.manuals); bumpPm();
      // Drop the candidate from the picker so we don't re-download
      setHunt(h => h ? ({ ...h, candidates: h.candidates.filter(x => x.url !== c.url) }) : h);
    } catch (e) { setErr(`Download failed: ${e.message}`); }
    finally { setDownloadingUrl(null); }
  }

  async function uploadManual(file) {
    if (!file) return;
    setUploading(true); setErr(''); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('type', manualType);
      fd.append('file', file);
      const r = await fetch(`/api/equipment/${id}/manuals`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      setUploadResult({
        page_count: j.page_count,
        pm_tasks_extracted: j.pm_tasks_extracted ?? 0,
        pm_extract_error: j.pm_extract_error ?? null,
      });
      await load();
      bumpPm();
    } catch (e) {
      setErr(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function reExtractPm(manualId) {
    setReExtractingId(manualId); setErr('');
    try {
      const r = await api.post(`/api/manuals/${manualId}/extract-pm`);
      setUploadResult({ page_count: null, pm_tasks_extracted: r.extracted, pm_extract_error: null, reExtract: true });
      await load();
      bumpPm();
    } catch (e) {
      setErr(`Re-extract failed: ${e.message}`);
    } finally {
      setReExtractingId(null);
    }
  }

  async function deleteManual(mid) {
    if (!confirm('Delete this manual?')) return;
    await api.delete(`/api/manuals/${mid}`);
    load();
  }

  async function searchManuals(e) {
    e.preventDefault();
    if (!searchQ.trim()) return;
    setSearching(true); setSearchHits(null); setQaAnswer(null);
    try {
      if (searchMode === 'qa') {
        const r = await api.post('/api/manuals/ask', { equipment_id: Number(id), question: searchQ });
        setQaAnswer(r);
      } else {
        const r = await api.get(`/api/manuals/search?equipment_id=${id}&q=${encodeURIComponent(searchQ)}`);
        setSearchHits(r.hits);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setSearching(false);
    }
  }

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;
  const e = data.equipment;

  async function addLog() {
    if (!logBody.trim()) return;
    await api.post(`/api/equipment/${id}/log`, { body: logBody.trim() });
    setLogBody('');
    load();
  }

  async function updateHours() {
    if (!hours) return;
    await api.post(`/api/equipment/${id}/hour-meter`, { hour_meter: Number(hours) });
    setHours('');
    load();
  }

  async function pmDone(taskId) {
    const notes = prompt('Notes (e.g. "used Rotella T6 5W-40")?') || null;
    const m = e.hour_meter != null ? Number(prompt(`Hour meter (current: ${e.hour_meter})?`, e.hour_meter)) : null;
    await api.post(`/api/pm-tasks/${taskId}/done`, { hour_meter: m || null, notes });
    load();
  }

  // Drop an extracted task that doesn't apply, before it ever goes active
  async function removePending(t) {
    if (!confirm(`Remove "${t.description}"? It won't be added to the schedule.`)) return;
    await api.patch(`/api/pm-tasks/${t.id}`, { status: 'deleted' });
    await load();
  }

  async function approvePm(taskId) {
    await api.post(`/api/pm-tasks/${taskId}/approve`);
    await load();
    bumpPm();
  }

  // askLastDone: after approving, walk through the tasks to record when each
  // was last done. Otherwise earlier services are assumed handled.
  async function approveAll(askLastDone = false) {
    const justApproved = data.pm_tasks.filter(t => t.status === 'pending_review');
    await api.post('/api/pm-tasks/approve-all', { equipment_id: Number(id) });
    if (askLastDone) setLastDoneTasks(justApproved);
    await load();
    bumpPm();
  }

  return (
    <div className="space-y-6">
      <Link to="/equipment" className="text-sm text-olive-700 hover:underline">‹ Equipment</Link>

      <header className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-serif text-olive-900">
              {e.year ? `${e.year} ` : ''}{e.make} {e.model}
            </h1>
            <div className="text-sm text-ink-700/70 mt-1">
              {e.category && <span>{e.category.replace('_', ' ')}</span>}
              {e.serial && <span> · SN {e.serial}</span>}
            </div>
          </div>
          {canManageEquipment && (
            <Link to={`/equipment/${id}/edit`} className="text-xs text-olive-700 hover:underline px-2 py-1">Edit</Link>
          )}
          {isOwner && (
            <button
              onClick={async () => {
                if (!confirm(
                  `Delete ${e.year ? `${e.year} ` : ''}${e.make} ${e.model}?\n\n` +
                  `THIS IS DESTRUCTIVE — all of the following are permanently removed:\n` +
                  `• All manuals + their indexed pages.\n` +
                  `• All PM tasks + their completion history.\n` +
                  `• All equipment-log entries.\n` +
                  `• Any child implements / attachments under this equipment have their parent link cleared (they survive on their own).\n\n` +
                  `Type "delete" in the next prompt to confirm.`,
                )) return;
                if (prompt(`Confirm: type "delete" to remove this equipment`) !== 'delete') return;
                try {
                  const r = await api.delete(`/api/equipment/${id}`);
                  const w = r.wiped || {};
                  alert(`✓ Deleted. Wiped ${w.manuals || 0} manuals, ${w.pm_tasks || 0} PM tasks, ${w.equipment_log || 0} log entries; cleared parent on ${w.child_equipment || 0} children.`);
                  navigate('/equipment');
                } catch (err) { setErr(err.message); }
              }}
              className="text-xs text-rust-700 hover:underline px-2 py-1"
            >
              Delete
            </button>
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-sm">
          <Field label="Hour meter">
            {e.hour_meter != null ? `${e.hour_meter} hr` : '—'}
            {e.hour_meter_updated_at && <span className="text-xs text-ink-700/60 block">updated {fmtDate(e.hour_meter_updated_at)}</span>}
          </Field>
          <Field label="Primary maintainer">{e.primary_maintainer_name || '—'}</Field>
          <Field label="Backup maintainer">{e.backup_maintainer_name || '—'}</Field>
          {isOwner && <Field label="Purchase date">{fmtDate(e.purchase_date)}</Field>}
          {isOwner && <Field label="Purchase price">{fmtMoney(e.purchase_price_cents)}</Field>}
        </dl>
        {e.notes && <div className="mt-3 text-sm text-ink-700/80 italic">{e.notes}</div>}
      </header>

      {data.children.length > 0 && (
        <section className="card">
          <h2 className="font-medium text-olive-900 mb-2">Implements / attachments</h2>
          <ul className="space-y-1 text-sm">
            {data.children.map(c => (
              <li key={c.id}>
                <Link to={`/equipment/${c.id}`} className="text-olive-700 hover:underline">
                  {c.make} {c.model} <span className="text-ink-700/50">({c.category})</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Pending-review approval card — only shown when extracted PM tasks await Owner approval */}
      {data.pm_tasks.some(t => t.status === 'pending_review') && (
        <section className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-olive-900">
              Extracted PM tasks awaiting approval ({data.pm_tasks.filter(t => t.status === 'pending_review').length})
            </h2>
          </div>
          {canManageEquipment && (
            <div className="bg-olive-50 rounded-lg p-3 space-y-2">
              <p className="text-sm text-ink-900">
                {e.hour_meter != null
                  ? <>This machine is at <strong>{e.hour_meter} hr</strong>. How should services before today be treated?</>
                  : <>No hour meter reading yet — set it below first so hour-based services start at the right point.</>}
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button onClick={() => approveAll(false)} className="btn-primary text-sm">
                  Assume they were done
                </button>
                <button onClick={() => approveAll(true)} className="btn-ghost text-sm">
                  Let me enter when each was last done
                </button>
              </div>
              <p className="text-xs text-ink-700/60">
                Either way the next service lands on the next interval mark above the current hours
                (a 250 hr service on a 900 hr machine comes due at 1000). Entering a last-done reading
                overrides that for a single task.
              </p>
            </div>
          )}
          <ul className="space-y-1 max-h-72 overflow-y-auto">
            {data.pm_tasks.filter(t => t.status === 'pending_review').map(t => (
              <li key={t.id} className="border-l-4 border-gold-500/40 pl-3 py-1 flex items-start gap-3">
                <div className="flex-1">
                  <div className="text-ink-900 text-sm">{t.description}</div>
                  <div className="text-xs text-ink-700/60 mt-0.5">
                    {t.interval_hours && <span>{t.interval_hours}hr</span>}
                    {t.interval_hours && t.interval_days && <span> or </span>}
                    {t.interval_days && <span>{t.interval_days}d</span>}
                  </div>
                </div>
                {canManageEquipment && (
                  <span className="flex items-center gap-1 shrink-0">
                    <button onClick={() => approvePm(t.id)} className="px-1.5 py-1 rounded text-xs text-olive-700 underline underline-offset-2 hover:bg-olive-50">approve</button>
                    <button onClick={() => removePending(t)} className="px-1.5 py-1 rounded text-xs text-rust-700 underline underline-offset-2 hover:bg-rust-500/10" aria-label={`Remove ${t.description}`}>remove</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {lastDoneTasks && (
        <LastDoneForm
          tasks={lastDoneTasks}
          hourMeter={e.hour_meter}
          onDone={async () => { setLastDoneTasks(null); await load(); bumpPm(); }}
        />
      )}

      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">Hour meter</h2>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="label">Current reading</label>
            <input className="input" type="number" step="0.1" value={hours} onChange={e => setHours(e.target.value)} placeholder={e.hour_meter ?? '0'} />
          </div>
          <button onClick={updateHours} className="btn-primary" disabled={!hours}>Update</button>
        </div>
        <UsageLine equipmentId={Number(id)} usage={data.usage} estimate={e.est_hours_per_week} canManage={canManageEquipment} onSaved={load} />
      </section>

      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">Manuals</h2>

        {canManageEquipment && (
          <div className="bg-olive-50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-end gap-3">
            <div>
              <label className="label">Type</label>
              <select className="input w-32" value={manualType} onChange={e => setManualType(e.target.value)}>
                <option value="operator">Operator</option>
                <option value="service">Service</option>
                <option value="parts">Parts</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="label">PDF</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                disabled={uploading}
                onChange={e => uploadManual(e.target.files?.[0])}
                className="block w-full text-sm text-ink-700 file:mr-3 file:rounded-md file:border-0 file:bg-olive-700 file:text-cream-50 file:px-3 file:py-2 file:font-medium hover:file:bg-olive-900"
              />
              <p className="text-xs text-ink-700/60 mt-1">
                {uploading ? 'Uploading + indexing + extracting PM intervals…' : 'Drop your operator/service PDF. Text + PM intervals auto-extracted by AI. 250MB max.'}
              </p>
            </div>
            <div>
              <button onClick={huntManuals} disabled={hunting} className="btn-ghost whitespace-nowrap">
                {hunting ? 'Searching…' : '🔍 Hunt manuals'}
              </button>
              <p className="text-xs text-ink-700/60 mt-1 max-w-[16rem]">
                AI search for the operator + service PDFs based on this equipment's make / model / serial.
              </p>
            </div>
          </div>
        )}

        {hunt && (
          <div className="rounded-lg border border-olive-100 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-olive-900">
                Hunt results for {[hunt.equipment.year, hunt.equipment.make, hunt.equipment.model].filter(Boolean).join(' ')}
              </div>
              <button className="text-xs text-ink-700/60 hover:text-rust-700" onClick={() => setHunt(null)}>dismiss</button>
            </div>
            {hunt.candidates.length === 0 ? (
              <p className="text-sm text-ink-700/60 italic">
                No candidates returned. Try filling in make/model/serial more precisely, or upload the PDF directly.
              </p>
            ) : (
              <ul className="space-y-2">
                {hunt.candidates.map((c, i) => (
                  <li key={i} className="flex items-start justify-between gap-2 text-sm border-b border-olive-100/40 pb-2 last:border-b-0 last:pb-0">
                    <div className="flex-1">
                      <div>
                        <span className="pill bg-cream-100 ring-1 ring-olive-100 text-ink-700 mr-2">{c.type}</span>
                        <span className="font-medium">{c.title}</span>
                      </div>
                      <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-olive-700 hover:underline break-all">
                        {c.url}
                      </a>
                      <span className="text-xs text-ink-700/50 ml-2">
                        {c.source}{c.confidence != null ? ` · conf ${(c.confidence * 100).toFixed(0)}%` : ''}
                      </span>
                    </div>
                    <button className="btn-primary text-xs py-1 px-2 whitespace-nowrap"
                            onClick={() => downloadCandidate(c)}
                            disabled={downloadingUrl === c.url}>
                      {downloadingUrl === c.url ? 'Downloading…' : 'Download'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {uploadResult && (
          <div className="rounded-lg bg-olive-100 text-olive-900 p-3 text-sm">
            ✓ {uploadResult.reExtract ? 'Re-extracted' : 'Uploaded'}
            {uploadResult.page_count != null && <span> — {uploadResult.page_count} pages indexed</span>}.
            {uploadResult.pm_tasks_extracted > 0 && (
              <> Found <strong>{uploadResult.pm_tasks_extracted} PM intervals</strong> — review &amp; approve in the PM schedule above.</>
            )}
            {uploadResult.pm_tasks_extracted === 0 && !uploadResult.pm_extract_error && (
              <> No PM intervals detected by AI. You can add tasks manually below.</>
            )}
            {uploadResult.pm_extract_error && (
              <div className="text-rust-700 mt-1 text-xs">PM extract error: {uploadResult.pm_extract_error}</div>
            )}
          </div>
        )}

        {manuals.length === 0 ? (
          <p className="text-sm text-ink-700/60 italic">No manuals uploaded yet.</p>
        ) : (
          <ul className="space-y-1">
            {manuals.map(m => (
              <li key={m.id} className="flex items-center gap-3 text-sm border-l-2 border-olive-100 pl-3 py-1">
                <span className={`pill ${m.type === 'service' ? 'bg-rust-500/10 text-rust-700' : 'pill-ok'}`}>{m.type}</span>
                <Link to={`/manuals/${m.id}`} className="text-olive-700 hover:underline flex-1">
                  Open {m.type} manual · {m.page_count} page{m.page_count === 1 ? '' : 's'}
                </Link>
                {canManageEquipment && (
                  <ManualOffsetInput manualId={m.id} value={m.pdf_page_offset || 0} onSaved={load} />
                )}
                <span className="text-xs text-ink-700/50">{fmtDate(m.created_at)}</span>
                {canManageEquipment && (
                  <button
                    onClick={() => reExtractPm(m.id)}
                    disabled={reExtractingId === m.id}
                    className="text-xs text-olive-700 hover:underline"
                    title="Re-run AI PM extraction on this manual"
                  >
                    {reExtractingId === m.id ? 'extracting…' : 'extract PM'}
                  </button>
                )}
                {canManageEquipment && (
                  <button onClick={() => deleteManual(m.id)} className="text-xs text-rust-700 hover:underline">delete</button>
                )}
              </li>
            ))}
          </ul>
        )}

        {manuals.length > 0 && (
          <div className="pt-3 border-t border-olive-100/60 space-y-3">
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                onClick={() => { setSearchMode('keyword'); setQaAnswer(null); setSearchHits(null); }}
                className={`pill cursor-pointer ${searchMode === 'keyword' ? 'bg-olive-700 text-cream-50' : 'bg-cream-100 text-ink-700'}`}
              >Keyword</button>
              <button
                type="button"
                onClick={() => { setSearchMode('qa'); setQaAnswer(null); setSearchHits(null); }}
                className={`pill cursor-pointer ${searchMode === 'qa' ? 'bg-olive-700 text-cream-50' : 'bg-cream-100 text-ink-700'}`}
              >Ask a question</button>
            </div>
            <form onSubmit={searchManuals} className="flex gap-2">
              <input
                className="input flex-1"
                placeholder={searchMode === 'qa'
                  ? 'e.g. "What hydraulic fluid does this take?", "How often do I change the air filter?"'
                  : 'Search manuals (e.g. "hydraulic fluid", "engine oil")'}
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
              />
              <button className="btn-primary" disabled={searching || !searchQ.trim()}>
                {searching ? '…' : searchMode === 'qa' ? 'Ask' : 'Search'}
              </button>
            </form>
            {searchHits && (
              <div className="space-y-1">
                {searchHits.length === 0 ? (
                  <p className="text-sm text-ink-700/60 italic">No matches.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {searchHits.map(h => (
                      <li key={h.id} className="border-l-2 border-gold-500/30 pl-3 py-1">
                        <Link to={`/manuals/${h.manual_id}?page=${h.page_num}&q=${encodeURIComponent(searchQ)}`} className="text-olive-700 hover:underline">
                          {h.type} manual · page {h.page_num}
                        </Link>
                        <div className="text-ink-700/80 text-xs mt-0.5" dangerouslySetInnerHTML={{ __html: safeSnippet(h.snippet) }} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {qaAnswer && (
              <div className="rounded-lg bg-olive-50 p-3 text-sm">
                <div className="text-ink-900 whitespace-pre-wrap">{qaAnswer.answer}</div>
                {qaAnswer.citations?.length > 0 && (
                  <div className="text-xs text-ink-700/60 mt-2">
                    Citations:{' '}
                    {qaAnswer.citations.map((c, i) => (
                      <Link key={i} to={`/manuals/${c.manual_id}?page=${c.page_num}`} className="text-olive-700 hover:underline mr-2">
                        {c.type} p{c.page_num}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">Notes log</h2>
        <div className="flex gap-2">
          <input className="input flex-1" value={logBody} onChange={e => setLogBody(e.target.value)} placeholder="Weird issue last week, oil leak near the…" />
          <button onClick={addLog} className="btn-primary" disabled={!logBody.trim()}>Add</button>
        </div>
        {data.log.length === 0 ? (
          <p className="text-sm text-ink-700/60 italic">No notes yet.</p>
        ) : (
          <ul className="space-y-2">
            {data.log.map(l => (
              <li key={l.id} className="border-l-2 border-olive-100 pl-3 py-1">
                <div className="text-sm text-ink-900">{l.body}</div>
                <div className="text-xs text-ink-700/60 mt-0.5">{l.user_name} · {fmtDate(l.created_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* PM schedule grid — Kubota-style interval chart at the bottom */}
      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">PM schedule</h2>
        <PmGrid equipmentId={Number(id)} canManage={canManageEquipment} onChange={() => { load(); bumpPm(); }} reloadSignal={pmRev} />
      </section>
    </div>
  );
}

// "Let me enter when each was last done" — one row per just-approved task.
// Blank rows keep the assumed-done baseline.
function LastDoneForm({ tasks, hourMeter, onDone }) {
  const [vals, setVals] = useState({});   // { [taskId]: { hours, date } }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  function set(taskId, k, v) { setVals(s => ({ ...s, [taskId]: { ...s[taskId], [k]: v } })); }

  async function save() {
    setBusy(true); setErr('');
    try {
      for (const t of tasks) {
        const v = vals[t.id] || {};
        if (!v.hours && !v.date) continue;
        try {
          await api.post(`/api/pm-tasks/${t.id}/backfill`, {
            last_done_hours: v.hours ? Number(v.hours) : null,
            last_done_at: v.date || null,
          });
        } catch (e) { throw new Error(`${t.description}: ${e.message}`); }
      }
      await onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">When was each service last done?</h2>
        <p className="text-sm text-ink-700/70">
          Fill in what you know{hourMeter != null ? <> (machine is at {hourMeter} hr)</> : null}. Leave a row blank to assume it was done on schedule.
        </p>
      </div>
      <ul className="space-y-2">
        {tasks.map(t => (
          <li key={t.id} className="flex flex-col sm:flex-row sm:items-center gap-2 border-l-4 border-gold-500/40 pl-3 py-1">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink-900">{t.description}</div>
              <div className="text-xs text-ink-700/60">
                every {[t.interval_hours && `${t.interval_hours} hr`, t.interval_days && `${t.interval_days} d`].filter(Boolean).join(' or ')}
              </div>
            </div>
            {t.interval_hours && (
              <input
                className="input w-full sm:w-36" type="number" min="0" step="0.1" inputMode="decimal"
                placeholder="last done at hr" aria-label={`${t.description}: last done at hours`}
                value={vals[t.id]?.hours || ''} onChange={ev => set(t.id, 'hours', ev.target.value)}
              />
            )}
            {t.interval_days && (
              <input
                className="input w-full sm:w-40" type="date" aria-label={`${t.description}: last done on date`}
                value={vals[t.id]?.date || ''} onChange={ev => set(t.id, 'date', ev.target.value)}
              />
            )}
          </li>
        ))}
      </ul>
      {err && <div className="text-rust-700 text-sm">{err}</div>}
      <div className="flex gap-2 justify-end">
        <button onClick={onDone} disabled={busy} className="btn-ghost text-sm">Skip — assume all done</button>
        <button onClick={save} disabled={busy} className="btn-primary text-sm">{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </section>
  );
}

// How fast this machine racks up hours — what the service calendar uses to put
// hour-based services on a date.
function UsageLine({ equipmentId, usage, estimate, canManage, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(estimate ?? '');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api.patch(`/api/equipment/${equipmentId}`, { est_hours_per_week: v === '' ? null : Number(v) });
      setEditing(false);
      await onSaved?.();
    } finally { setBusy(false); }
  }
  const perWeek = usage?.per_week != null ? Math.round(usage.per_week * 10) / 10 : null;
  return (
    <div className="text-xs text-ink-700/70 border-t border-olive-100/60 pt-2 flex items-center gap-2 flex-wrap">
      <span>
        {usage?.source === 'history' && <>Runs about <strong>{perWeek} hr/week</strong> (from recent readings) — used to date services on the <Link to="/calendar" className="text-olive-700 hover:underline">calendar</Link>.</>}
        {usage?.source === 'estimate' && <>Estimated at <strong>{perWeek} hr/week</strong> — real readings take over after a few weeks. Used by the <Link to="/calendar" className="text-olive-700 hover:underline">calendar</Link>.</>}
        {!usage?.source && <>No usage rate yet, so hour-based services can't be put on the <Link to="/calendar" className="text-olive-700 hover:underline">calendar</Link>.</>}
      </span>
      {canManage && !editing && usage?.source !== 'history' && (
        <button onClick={() => setEditing(true)} className="text-olive-700 hover:underline">{estimate ? 'change estimate' : 'add estimate'}</button>
      )}
      {editing && (
        <span className="inline-flex items-center gap-1">
          <input className="input w-20 py-1" type="number" min="0" step="0.5" inputMode="decimal" value={v} onChange={ev => setV(ev.target.value)} aria-label="Typical hours per week" />
          <span>hr/week</span>
          <button onClick={save} disabled={busy} className="btn-primary text-xs py-1 px-2">Save</button>
          <button onClick={() => setEditing(false)} className="text-ink-700/60 hover:underline">cancel</button>
        </span>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-700/60">{label}</dt>
      <dd className="text-ink-900 mt-0.5">{children}</dd>
    </div>
  );
}

function ManualOffsetInput({ manualId, value, onSaved }) {
  const [v, setV] = useState(String(value));
  const [busy, setBusy] = useState(false);
  async function save() {
    if (Number(v) === Number(value)) return;
    setBusy(true);
    try {
      await api.patch(`/api/manuals/${manualId}`, { pdf_page_offset: Number(v) || 0 });
      onSaved?.();
    } finally { setBusy(false); }
  }
  return (
    <span
      className="text-xs text-ink-700/60 inline-flex items-center gap-1"
      title="Pages of front matter (cover, TOC) before printed page 1. The manual's printed pages = PDF index − this offset."
    >
      offset
      <input
        type="number"
        min="0"
        max="50"
        value={v}
        onChange={e => setV(e.target.value)}
        onBlur={save}
        disabled={busy}
        className="w-12 rounded border border-olive-100 bg-cream-50 px-1 py-0.5 text-center"
      />
    </span>
  );
}
